import type { MessageDescriptor } from 'react-intl';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { containsNetworkError } from '@confluence/network';

import { ObjectCommentsError } from './enum/ObjectCommentsError.ts';
import { i18n } from './objectCommentsi18n.ts';

type ParsedError = {
	message: string;
	statusCode?: number;
};

/**
 * Returns the index at which the <REASON> of a `GraphQL error: <GUID>:[ <EXCEPTION>:] <REASON>`
 * message starts.
 *
 * The reason is delimited from the wrapper by the *last* colon, but a reason can legitimately
 * contain a URL (for example the support link in `ObjectCommentsError.STEP_UP_AUTH_REQUIRED`).
 * The colon in `https://` must not be mistaken for that delimiter, so only the portion of the
 * message preceding the first URL is searched.
 *
 * URL-aware delimiter detection is gated behind the
 * 'confluence_fe_abort_comment_on_access_policy_err' experiment: the access-policy errors are the
 * only reasons that carry a URL today, so with the experiment off every message keeps its previous
 * (whole-message `lastIndexOf`) behaviour.
 */
export const getGraphQLReasonStartIdx = (message: string): number => {
	const urlStartIdx = message.search(/\bhttps?:\/\//);
	const delimiterSearchScope =
		urlStartIdx === -1 ||
		!expValEquals('confluence_fe_abort_comment_on_access_policy_err', 'isEnabled', true)
			? message
			: message.slice(0, urlStartIdx);

	return delimiterSearchScope.lastIndexOf(':') + 1;
};

const parseError: (error: Error | ApolloErrorInterop) => ParsedError = (error) => {
	const { message } = error;
	let errorMessage: string;

	/* If this is a GraphQL error we need to extract the data out of it
	 * GraphQL errors that are returned from queries/mutations always have the same format:
	 * GraphQL error: <GUID>:[ <EXCEPTION>:] <REASON>
	 */
	if (message.indexOf('GraphQL error:') !== -1) {
		const reasonStartIdx = getGraphQLReasonStartIdx(message);
		errorMessage = message.substr(reasonStartIdx, message.length - reasonStartIdx).trim();
	} else {
		errorMessage = message;
	}

	const parsedError: ParsedError = {
		message: errorMessage,
	};

	// If we have an ApolloErrorInterop, extract the status code and the authorization status
	const { graphQLErrors } = error as ApolloErrorInterop;
	if (graphQLErrors && graphQLErrors.length) {
		parsedError.statusCode = graphQLErrors?.[0]?.extensions?.statusCode;
	}

	return parsedError;
};

export const getTranslatedError: (
	error: Error | ApolloErrorInterop,
) => Required<MessageDescriptor> = (error) => {
	if (containsNetworkError(error)) {
		return i18n.networkError;
	}

	const parsedError = parseError(error);
	const { message } = parsedError;
	switch (true) {
		case message === ObjectCommentsError.VIEW_NOT_PERMITTED:
			return i18n.viewNotPermitted;
		case message === ObjectCommentsError.NO_CREATE_PERMS:
			return i18n.createNotPermitted;
		case isRawContentNotFoundError(message):
			return i18n.contentNotFound;
		case message === ObjectCommentsError.STEP_UP_AUTH_REQUIRED:
			return i18n.stepUpAuthRequired;
		case message === ObjectCommentsError.IP_ALLOWLIST_BLOCKED:
			return i18n.ipAllowlistBlocked;
		default:
			return i18n.unexpectedError;
	}
};

export const isViewNotPermittedError = (translatedMessage: MessageDescriptor): boolean =>
	translatedMessage.id === i18n.viewNotPermitted.id;

export const isNetworkError = (translatedMessage: MessageDescriptor): boolean =>
	translatedMessage.id === i18n.networkError.id;

export const isCreateNotPermittedError = (translatedMessage: MessageDescriptor): boolean =>
	translatedMessage.id === i18n.createNotPermitted.id;

export const isContentNotFoundError = (translatedMessage: MessageDescriptor): boolean =>
	translatedMessage.id === i18n.contentNotFound.id;

// Step-up authentication (Atlassian External User Security) and IP-allowlist rejections are
// admin-configured, user/admin-attributed policy errors. They are enforced per-request at the
// GraphQL gateway and bite on the save mutation, so they are not Atlassian reliability faults.
export const isStepUpAuthError = (translatedMessage: MessageDescriptor): boolean =>
	translatedMessage.id === i18n.stepUpAuthRequired.id;

export const isIpAllowlistError = (translatedMessage: MessageDescriptor): boolean =>
	translatedMessage.id === i18n.ipAllowlistBlocked.id;

export const isCommentAbortableError = (translatedMessage: MessageDescriptor): boolean =>
	isCreateNotPermittedError(translatedMessage) ||
	isViewNotPermittedError(translatedMessage) ||
	isContentNotFoundError(translatedMessage) ||
	// Only the add-page-comment path consumes isCommentAbortableError today, so gating the new
	// access-policy errors here keeps the abort/fail reclassification scoped to that experience
	// (the global getExperienceStopReason is intentionally left untouched). The experiment is
	// evaluated last so exposure is only fired for the access-policy errors it actually gates.
	((isStepUpAuthError(translatedMessage) || isIpAllowlistError(translatedMessage)) &&
		expValEquals('confluence_fe_abort_comment_on_access_policy_err', 'isEnabled', true));

const CONTENT_NOT_FOUND_PATTERNS = [
	/Content with id \S+ for comment container is not found/,
	/No content found with id\s*:\s*\S+/,
	/Cannot create a \[[^\]]+\] comment on content with \[(?:trashed|deleted|archived)\] status/,
];

/**
 * The footer-comment flavour of the container NotFoundException, e.g.
 * `Attempted to create [FOOTER] comment, but container content with id 12345 and content status
 * [current] for comment container is not found.`
 *
 * The `[current]` status is matched explicitly: a non-`[current]` status (for example `[draft]`)
 * is not the "the page is gone" case this classification is for.
 *
 * Kept out of CONTENT_NOT_FOUND_PATTERNS because it is gated behind the
 * 'confluence_fe_comment_container_content_not_found' experiment — with the experiment off these
 * messages keep their previous `unexpectedError` classification.
 */
const GATED_COMMENT_CONTAINER_NOT_FOUND_PATTERN =
	/content with id \S+ and content status \[current\] for comment container is not found/;

export const isRawContentNotFoundError = (errorMessage: string): boolean =>
	CONTENT_NOT_FOUND_PATTERNS.some((pattern) => pattern.test(errorMessage)) ||
	// The experiment is evaluated last so exposure is only fired for the comment-container
	// messages it actually gates, not for every error that passes through here.
	(GATED_COMMENT_CONTAINER_NOT_FOUND_PATTERN.test(errorMessage) &&
		expValEquals('confluence_fe_comment_container_content_not_found', 'isEnabled', true));
